import React, { useContext, useState } from 'react';
import { DeleteOutlined, ExclamationCircleOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Tooltip, message } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ShopCategoriesType } from '../../../../types/shopCategories';
import { SHOP_CATEGORIES } from '../../../../constants/define/translation';
import { deleteShopCategoriesRepository } from '../../../../repository/mytm/shop-categories/shopCategoriesRepository';
import { btnActive } from '../../../../styles';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { UserContext } from '../../../../repository/context/userContext';

interface ActionsColumnProps {
  record: ShopCategoriesType;
  onRefresh: () => void;
  total: number;
  handleChangePageActive: Function;
  removeItemByIndex: Function;
  setLoading: (value: boolean) => void;
  handleChangeEditMode: Function;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(SHOP_CATEGORIES);
  const userContext = useContext(UserContext);
  const [deleting, setDeleting] = useState(false);
  const { record, onRefresh, handleChangePageActive, total, setLoading, removeItemByIndex, handleChangeEditMode } =
    props;
  const handleDelete = async () => {
    if (!record.id) {
      removeItemByIndex(record.index);
      return;
    }
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.delete')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setDeleting(true);
        const updateFunc = () => {
          deleteShopCategoriesRepository(record?.id)
            .then(res => {
              message.success(t('message.success.deleteShopCategory'));
            })
            .catch(() => message.error(MESSAGE_ERROR.M02))
            .finally(() => {
              const numRecord = total - 1;
              if (numRecord && numRecord > 0) {
                onRefresh();
              } else {
                handleChangePageActive(1);
              }
              setDeleting(false);
            });
        };
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setDeleting(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleEdit = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.edit')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading && setLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              handleChangeEditMode(record.index);
            })
            .catch((e: any) => {
              message.error(e?.errorMessage);
            })
            .finally(() => {
              setLoading && setLoading(false);
            });
        }
      },
      onCancel() {
        // NOT
      },
    });
  };

  return (
    <StyledContainer>
      <Space size="small">
        <Tooltip title={t('table.tooltip.edit')}>
          <Button icon={<EditOutlined />} onClick={handleEdit} disabled={!record.id || record.isInEditMode} />
        </Tooltip>
        <Tooltip title={t('table.tooltip.delete')}>
          <Button
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            disabled={deleting || !record.id}
            loading={deleting}
          />
        </Tooltip>
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 0.5rem;
  ${btnActive}

  .delete-button .ant-btn[disabled] {
    color: rgba(0, 0, 0, 1);
  }
`;
